const {
    filter,
    findLast,
    get,
    isEmpty,
    merge,
    replace,
} = require('lodash');

const SUBSCRIPTION_UPDATING_STATE_ID = 'SubscriptionUpdating';

/**
 * Delivery Saver Payment Setup Component.
 * Note 1: Avoid adding payment type specific concerns in this component.
 *         i.e. This component does not need to know about specific payment instrument types (e.g. giftCard or Paypal)
 *         All payment type specific logic should be part of the instruments component.
 */
class DeliverySaverPaymentUpdateController {
    constructor(
        $element,
        $q,
        $state,
        settings,
        announcerService,
        digitalPayPaymentErrorDisplayService,
        loadingIndicatorService,
        digitalPayIframeEventHandler,
        digitalPayPaymentDistributionService,
        digitalPayPrepaymentActionsService,
        digitalPayDisablePaymentStateService,
        digitalPayPaymentErrorStateService,
        digitalPayErrorMessageGenerator,
        deliverySaverService,
    ) {
        this.$element = $element;
        this.$q = $q;
        this.$state = $state;
        this.settings = settings;
        this.announcerService = announcerService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.loadingIndicatorService = loadingIndicatorService;
        this.iframeEventHandler = digitalPayIframeEventHandler;
        this.paymentDistributionService = digitalPayPaymentDistributionService;
        this.prePaymentActionsService = digitalPayPrepaymentActionsService;
        this.disablePaymentStateService = digitalPayDisablePaymentStateService;
        this.digitalPayPaymentErrorStateService = digitalPayPaymentErrorStateService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
        this.deliverySaverService = deliverySaverService;
    }

    $onInit() {
        this.isInitialised = false;
        this.isCvvDialogEnabled = false;
        this.iframeEventHandler.init();
        this.prePaymentActionsService.init();
        this.disablePaymentStateService.init();
        this.digitalPayPaymentErrorStateService.init();

        this.prePaymentActionDefinitions = {
            addAction: this.prePaymentActionsService.addAction.bind(this.prePaymentActionsService),
            removeAction: this.prePaymentActionsService.removeAction.bind(this.prePaymentActionsService),
        };

        this.paymentStateDefinitions = {
            addDisablePaymentState: this.disablePaymentStateService.addDisablePaymentState.bind(this.disablePaymentStateService),
            removeDisablePaymentState: this.disablePaymentStateService.removeDisablePaymentState.bind(this.disablePaymentStateService),
        };

        this.isInitialised = true;
    }

    $onDestroy() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.iframeEventHandler.dispose();
        this.prePaymentActionsService.removeAllActions();
        this.digitalPayPaymentErrorStateService.clearErrors();
    }

    /**
     * Notifies when child instrument components are in manage mode.
     * @param {*boolean} state
     */
    notifyWhenChildIsInManageMode(state) {
        // The field here is an additional field used for showing the account password textbox
        this.anyChildIsInManageMode = state;
    }

    shouldDisableUpdate() {
        return this.disablePaymentStateService.hasDisablePaymentStates() || !this.paymentDistributionService.hasSingleSelectType();
    }

    updatePaymentInstrument() {
        // logic from digital-pay-button
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();

        if (this.shouldDisableUpdate()) {
            this.announcerService.announce(replace(this.settings.digitalPayScreenReaderMessages.placeOrderInvalidDisableMessage, '{0}', this.disablePaymentStateService.getReasonsWhyDisabled()));
            return;
        }

        this.paymentStateDefinitions.addDisablePaymentState(SUBSCRIPTION_UPDATING_STATE_ID, this.settings.digitalPayScreenReaderMessages.subscriptionProcessingDisableMessage);
        this.resetPaymentStates();
        const prePaymentActions = this.prePaymentActionsService.getAllActions();

        const prePaymentPromises = [];
        prePaymentActions.forEach((action) => {
            prePaymentPromises.push(action());
        });

        const paymentUpdatePromise = paymentInstrument => this.deliverySaverService
            .updatePaymentInstrument(paymentInstrument)
            .then(() => {
                this.$state.go('shop.myaccount.deliveryplus');
            })
            .catch(response => this.handlePaymentUpdateFailure(response));

        this.loadingIndicatorService.startLoading(true, 'Updating your payment details', false);

        // Once all prepayment promises are resolved update the payment method.
        this.$q.all(prePaymentPromises)
            .then(prePaymentResults => this.getPaymentInstrument(prePaymentResults))
            .then(paymentUpdatePromise)
            .catch((error) => {
                this.hasPrePaymentActionFailures = true;
                if (error && error.errorMessage) {
                    this.digitalPayPaymentErrorDisplayService.pushGlobalError(error.errorMessage);
                }
            })
            .finally(() => {
                this.loadingIndicatorService.finishLoading();
                this.paymentStateDefinitions.removeDisablePaymentState(SUBSCRIPTION_UPDATING_STATE_ID);
            });
    }

    /**
     * Returns a paymentInstrument object
     * which is a merging of all the results for the instrument being used
     * @param {Promise[]} prePaymentResults
     * @returns {Object} paymentInstrument with paymentInstrumentId and stepUpToken
     */
    getPaymentInstrument(prePaymentResults) {
        let paymentInstrumentId = this.paymentDistributionService.getSingleSelectKey();
        if (this.paymentDistributionService.isPayingWithNewCard()) {
            // There should only be one prePaymentResult
            // but double check for a stepUpToken to ensure we use the right one
            paymentInstrumentId = get(findLast(prePaymentResults, result => result.paymentInstrumentId && result.stepUpToken), 'paymentInstrumentId');
        }
        if (paymentInstrumentId != null) {
            const paymentInstrumentResults = filter(prePaymentResults, result => result.paymentInstrumentId === paymentInstrumentId);
            const paymentInstrument = merge(...paymentInstrumentResults);
            if (!isEmpty(paymentInstrument)) {
                return paymentInstrument;
            }
        }
        return this.$q.reject({
            errorMessage: this.settings.deliverySaver.paymentUpdateGenericError,
        });
    }

    /**
     * For clearing errors and resetting prepayment states
     */
    resetPaymentStates() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.hasPrePaymentActionFailures = false;
        this.hasPrePaymentValidationFailure = false;
        this.shouldRemoveNewlyAddedInstruments = false;
        this.digitalPayErrorMessageGenerator.setPaymentErrorMode(false);
    }

    handlePaymentUpdateFailure(response) {
        const errorMessage = (response.status >= 500) ?
            this.settings.deliverySaver.paymentUpdateServerError :
            this.settings.deliverySaver.paymentUpdateGenericError;
        this.digitalPayPaymentErrorDisplayService.pushGlobalError(errorMessage);
        this.shouldRemoveNewlyAddedInstruments = true;
    }
}

DeliverySaverPaymentUpdateController.$inject = [
    '$element',
    '$q',
    '$state',
    'Settings',
    'AnnouncerService',
    'DigitalPayPaymentErrorDisplayService',
    'LoadingIndicatorService',
    'DigitalPayIframeEventHandler',
    'DigitalPayPaymentDistributionService',
    'DigitalPayPrepaymentActionsService',
    'DigitalPayDisablePaymentStateService',
    'DigitalPayPaymentErrorStateService',
    'DigitalPayErrorMessageGenerator',
    'DeliverySaverService',
];

const DeliverySaverPaymentUpdateComponentDefinition = {
    controller: DeliverySaverPaymentUpdateController,
    template: require('./delivery-saver-payment-update.component.html').trim(),
};

module.exports = {
    DeliverySaverPaymentUpdateController,
    DeliverySaverPaymentUpdateComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/deliverysaver/paymentupdate/delivery-saver-payment-update.component.js